import { Fade } from '@material-ui/core';
import LoadingOverlay from 'app/shared/page-parts/LoadingOverlay';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { Classes, TransientPick } from 'app/tools/general.types';
import {
  usePreviousValue,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { Merge } from 'type-fest';

type ContainerOpts = TransientPick<Props, 'height' | 'maxWidth'> & {
  $hide?: boolean;
  $noPad?: boolean;
};
const Container = styled.div<ContainerOpts>`
  align-self: stretch;
  padding: ${p => (p.$noPad ? 0 : styledFuncs.pad())};
  position: relative;

  display: ${p => (p.$hide ? 'none' : 'flex')};
  justify-content: center;
  align-items: center;

  > * {
    max-width: 100%;
    max-height: 100%;
    overflow: hidden;
    ${p =>
      p.$height &&
      css`
        height: ${p.$height};
      `}
    ${p =>
      p.$maxWidth &&
      css`
        max-width: ${p.$maxWidth};
      `}
  }
`;

const FadeOverlay = styled.div`
  position: absolute;
  background: white;
  ${styledMixins.fullsize}
`;

function isCached(src: string): boolean {
  const img = new Image();
  img.src = src;
  const { complete } = img;
  img.src = '';
  return complete;
}

class SrcList {
  srcStore: string[];

  constructor() {
    this.srcStore = [];
  }

  check(src?: string) {
    return !!src && this.srcStore.includes(src);
  }

  add(src?: string) {
    if (src) this.srcStore = _.union(this.srcStore, [src]);
  }

  del(src?: string) {
    if (src) this.srcStore = _.difference(this.srcStore, [src]);
  }
}

const brokenSrcs = new SrcList();

type ImgProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

type Props = Merge<
  ImgProps,
  {
    src?: string;
    alt: string;
    fallbackImgNode?: React.ReactNode;
    height?: string;
    maxWidth?: string;
    noPad?: boolean;
    onLoadShouldError?: (
      e: React.SyntheticEvent<HTMLImageElement, Event>
    ) => boolean;
    onFallbackUsed?: () => void;
    pendingSrc?: boolean;
    classes?: Classes<'img'>;
  }
>;

export default function AppImage({
  src,
  alt,
  fallbackImgNode,
  className,
  height,
  maxWidth,
  noPad,
  onLoadShouldError,
  onError,
  onLoad,
  onFallbackUsed,
  pendingSrc,
  classes,
  ...imgProps
}: Props): JSX.Element {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const srcAtStart = useMemo(() => src, []);
  const preCached = useMemo(() => !!src && isCached(src), [src]);
  const preErrored = useMemo(() => brokenSrcs.check(src), [src]);

  const onFallbackUsedRef = useWatchAsRef(onFallbackUsed);

  // false if error, true if loaded, undefined if not loaded yet
  const preLoadSuccessful = !preErrored && (preCached || undefined);
  const [loadSuccessful, setLoadSuccessful] =
    useState(preLoadSuccessful);

  useEffect(() => {
    setLoadSuccessful(preLoadSuccessful);
  }, [preLoadSuccessful]);

  const onErrorHandler: ImgProps['onError'] = e => {
    e.preventDefault();
    onError?.(e);
    setLoadSuccessful(false);
    brokenSrcs.add(src);
  };

  const onLoadHandler: ImgProps['onLoad'] = e => {
    if (onLoadShouldError?.(e)) {
      onErrorHandler(e);
    } else {
      onLoad?.(e);
      setLoadSuccessful(true);
      brokenSrcs.del(src);
    }
  };

  const showFallback =
    (!pendingSrc && !src) || loadSuccessful === false;
  const disableFading =
    src === srcAtStart && (preCached || showFallback);
  const disableFadingPrev = usePreviousValue(
    disableFading,
    disableFading
  );
  useEffect(() => {
    // NOTE: only runs when showFallback changes
    if (showFallback) onFallbackUsedRef.current?.();
  }, [onFallbackUsedRef, showFallback]);

  const imageFadedOut = !showFallback && !loadSuccessful;
  const hideWholeImage = !src && !fallbackImgNode && !height;

  return (
    <Container
      $height={height}
      $maxWidth={maxWidth}
      $hide={hideWholeImage}
      $noPad={noPad}
      {...{ className }}
    >
      {!disableFading && (
        <Fade in={imageFadedOut} appear={disableFadingPrev}>
          <FadeOverlay />
        </Fade>
      )}
      <LoadingOverlay noPortal loadingComplete={!pendingSrc} />
      {(showFallback && fallbackImgNode) || (
        <img
          alt={alt}
          onError={onErrorHandler}
          onLoad={onLoadHandler}
          className={classes?.img || 'block object-contain'}
          {...{ src }}
          {...imgProps}
        />
      )}
    </Container>
  );
}
